'use client';

import { useSearchParams } from 'next/navigation';
import { type FC, useCallback, useState } from 'react';

import {
  RegularAuth,
  type RegularAuthFormData,
  type RegularAuthFormHandler,
} from '@/app/[locale]/(not-authorized)/account/login/components/Login/components/RegularAuth';
import {
  type CodeFormHandler,
  TwoFaAuth,
} from '@/app/[locale]/(not-authorized)/account/login/components/Login/components/TwoFaAuth';
import { TwoFaSetup } from '@/app/[locale]/(not-authorized)/account/login/components/Login/components/TwoFaSetup';
import { urls } from '@/constants/urls';
import { useAuth } from '@/hooks/auth/use-auth';
import { useBoolean } from '@/hooks/use-boolean';
import { useDeviceProperties } from '@/hooks/use-device-properties';
import { useRouter } from '@/i18n/routing';

export const Login: FC = () => {
  const [regularAuthData, setRegularAuthData] = useState<RegularAuthFormData>({
    username: '',
    password: '',
  });
  const { value: is2Fa, setTrue: start2Fa, setFalse: end2Fa } = useBoolean();
  const [is2FaSetup, setIs2FaSetup] = useState(false);

  const { isMobile } = useDeviceProperties();

  const { login } = useAuth(is2Fa);

  const router = useRouter();
  const searchParams = useSearchParams();

  const redirect = useCallback(() => {
    router.push({
      pathname: searchParams?.get('returnUrl') ?? urls.Dashboard,
    });
  }, [router, searchParams]);

  const handleRegularAuthSubmit = useCallback<RegularAuthFormHandler>(
    async (data) => {
      setRegularAuthData(data);

      const { data: result, customErrors } = await login(data);

      if (!result) {
        return;
      }

      const is2FaError = customErrors?.includes('2fa');

      if (is2FaError) {
        start2Fa();

        return;
      }

      if (isMobile) {
        redirect();
      } else {
        setIs2FaSetup(true);
      }
    },
    [isMobile, login, redirect, start2Fa]
  );

  const handle2FaAuthSubmit = useCallback<CodeFormHandler>(
    async (data) => {
      const { data: result, customErrors } = await login({
        ...regularAuthData,
        token: data.code,
      });

      if (!result || customErrors?.length) {
        return;
      }

      redirect();
    },
    [login, redirect, regularAuthData]
  );

  return is2FaSetup ? (
    <TwoFaSetup redirect={redirect} />
  ) : is2Fa ? (
    <TwoFaAuth onSubmit={handle2FaAuthSubmit} onCancel={end2Fa} />
  ) : (
    <RegularAuth onSubmit={handleRegularAuthSubmit} />
  );
};
